import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  actualTheme: "light" | "dark"; // The actual theme being displayed (resolves "system")
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Theme Provider
 *
 * Provides dark mode support with:
 * - Light, dark, and system preference modes
 * - LocalStorage persistence
 * - Automatic system preference detection
 * - CSS class-based theming (Tailwind dark mode)
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Initialize from localStorage or default to system
    if (typeof window !== "undefined") {
      return (localStorage.getItem("theme") as Theme) || "system";
    }
    return "system";
  });

  const [actualTheme, setActualTheme] = useState<"light" | "dark">("light");

  // Detect system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const updateActualTheme = () => {
      if (theme === "system") {
        setActualTheme(mediaQuery.matches ? "dark" : "light");
      } else {
        setActualTheme(theme);
      }
    };

    // Initial update
    updateActualTheme();

    // Listen for system preference changes
    mediaQuery.addEventListener("change", updateActualTheme);

    return () => mediaQuery.removeEventListener("change", updateActualTheme);
  }, [theme]);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove both classes first
    root.classList.remove("light", "dark");

    // Add the actual theme class
    root.classList.add(actualTheme);

    // Update meta theme-color for mobile browsers. These match the
    // --background tokens in index.css (hsl 224 14% 17% → #24272f for
    // dark, hsl 220 20% 95% → #eef0f5 for light). Keep in sync with
    // public/theme-init.js which sets these before first paint.
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        actualTheme === "dark" ? "#24272f" : "#eef0f5",
      );
    }
  }, [actualTheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, actualTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 *
 * @example
 * ```tsx
 * const { theme, setTheme, actualTheme } = useTheme();
 *
 * return (
 *   <button onClick={() => setTheme("dark")}>
 *     Current: {actualTheme}
 *   </button>
 * );
 * ```
 */
export function useTheme() {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
}
